import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, Chip, TablePagination, IconButton, Tooltip
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { LeaveBalanceService } from '../../services/leave-balance.service';
import { LeaveTypeService } from '../../services/leave-type.service';
import { AccrualRule, UserLeaveBalanceSummary } from '../../interfaces/leave.interface';
import { useToast } from '../../context/ToastContext';
import { displayPersonName } from '../../utils/userUtils';
import AccrualHistoryDrawer from './AccrualHistoryDrawer';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

interface AccrualLeaveTypeMeta {
    id: string;
    accrualRule: AccrualRule;
}

const AllUserLeaveBalances: React.FC = () => {
    const [summaries, setSummaries] = useState<UserLeaveBalanceSummary[]>([]);
    // Fix round 1 / F10: keyed on name (the only field LeaveBalanceDTO carries) but now also
    // holds accrualRule, so the history button can be gated to types where accrual is actually
    // enabled instead of appearing for every resolvable type.
    const [leaveTypeMetaByName, setLeaveTypeMetaByName] = useState<Record<string, AccrualLeaveTypeMeta>>({});
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [drawerTarget, setDrawerTarget] = useState<
        { userId: string; userName: string; leaveTypeId: string; leaveTypeName: string } | null
    >(null);
    const toast = useToast();
    // L-1 fix: the history endpoint requires LEAVE_ALLOCATION_VIEW (spec §2.3), a DIFFERENT
    // permission than the grid itself (LEAVE_BALANCE_VIEW_ALL) -- a role holding one and not the
    // other must not see an icon that 403s when clicked.
    const canViewAccrualHistory = usePermission(PERMISSIONS.LEAVE_ALLOCATION_VIEW);

    useEffect(() => {
        fetchBalances();
        fetchLeaveTypes();
    }, []);

    const fetchBalances = async () => {
        try {
            const data = await LeaveBalanceService.getAllUserBalances();
            setSummaries(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to fetch leave balances');
        } finally {
            setLoading(false);
        }
    };

    // The balance grid's LeaveBalanceDTO carries only the leave type's NAME (spec §2.4's
    // LeaveBalanceDTO shape) — the accrual history endpoint needs the id, so resolve it here
    // once via the existing leave-type list rather than adding a name param to the history route.
    const fetchLeaveTypes = async () => {
        try {
            const types = await LeaveTypeService.getByInstitution();
            setLeaveTypeMetaByName(
                Object.fromEntries(types.map((t) => [
                    t.name,
                    { id: t.id, accrualRule: t.accrualRule ?? AccrualRule.NONE },
                ]))
            );
        } catch (err) {
            toast.showApiError(err, 'Failed to fetch leave types');
        }
    };

    const leaveTypes = Array.from(new Set(summaries.flatMap(s => s.balances.map(b => b.leaveType))));
    const paginatedSummaries = summaries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (loading) return <Typography>Loading...</Typography>;

    return (
        <Box data-help-page="leave.balances.page" sx={{ padding: 3 }}>
            <Typography variant="h5" sx={{ marginBottom: 3 }}>All User Leave Balances</Typography>

            <TableContainer data-help-section="leave.balances.table" component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="leave.balances.col.user" rowSpan={2}>User</TableCell>
                            {leaveTypes.map((type) => (
                                <TableCell key={type} data-help="leave.balances.col.leaveType" colSpan={3} align="center">{type}</TableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            {leaveTypes.map((type) => (
                                <React.Fragment key={type}>
                                    <TableCell data-help="leave.balances.col.allocated" align="center">Allocated</TableCell>
                                    <TableCell data-help="leave.balances.col.used" align="center">Used</TableCell>
                                    <TableCell data-help="leave.balances.col.remaining" align="center">Remaining</TableCell>
                                </React.Fragment>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedSummaries.map((summary) => (
                            <TableRow key={summary.userId}>
                                <TableCell>{displayPersonName(summary.userDisplayName, summary.userName)}</TableCell>
                                {leaveTypes.map((type) => {
                                    const balance = summary.balances.find(b => b.leaveType === type);
                                    if (!balance) {
                                        return (
                                            <React.Fragment key={type}>
                                                <TableCell align="center">0</TableCell>
                                                <TableCell align="center">0</TableCell>
                                                <TableCell align="center">0</TableCell>
                                            </React.Fragment>
                                        );
                                    }
                                    return (
                                        <React.Fragment key={type}>
                                            <TableCell align="center">
                                                {balance.allocated}
                                                {balance.isOverride && (
                                                    <Chip data-help="leave.balances.override" label="Override" size="small" color="info" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />
                                                )}
                                            </TableCell>
                                            <TableCell align="center">{balance.availed}</TableCell>
                                            <TableCell align="center" sx={{ color: balance.remaining < 0 ? 'error.main' : 'inherit', fontWeight: balance.remaining < 0 ? 'bold' : 'normal' }}>
                                                {balance.remaining}
                                                {canViewAccrualHistory && leaveTypeMetaByName[type]?.accrualRule === AccrualRule.PER_DAYS_WORKED && (
                                                    <Tooltip title="Accrual history">
                                                        <IconButton
                                                            size="small"
                                                            data-help="leave.balances.action.accrualHistory"
                                                            aria-label={`Accrual history — ${displayPersonName(summary.userDisplayName, summary.userName)} — ${type}`}
                                                            onClick={() => setDrawerTarget({
                                                                userId: summary.userId,
                                                                userName: displayPersonName(summary.userDisplayName, summary.userName),
                                                                leaveTypeId: leaveTypeMetaByName[type].id,
                                                                leaveTypeName: type,
                                                            })}
                                                        >
                                                            <HistoryIcon fontSize="inherit" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </React.Fragment>
                                    );
                                })}
                            </TableRow>
                        ))}
                        {summaries.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={1 + leaveTypes.length * 3} align="center">
                                    No leave data available
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                data-help="leave.balances.paging"
                component="div"
                count={summaries.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 20, 50, 100]}
            />
            {drawerTarget && (
                <AccrualHistoryDrawer
                    open={!!drawerTarget}
                    onClose={() => setDrawerTarget(null)}
                    userId={drawerTarget.userId}
                    userName={drawerTarget.userName}
                    leaveTypeId={drawerTarget.leaveTypeId}
                    leaveTypeName={drawerTarget.leaveTypeName}
                    onMutated={fetchBalances}
                />
            )}
        </Box>
    );
};

export default AllUserLeaveBalances;
